import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCreditCard } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { Order, OrderStatus } from "../../../core/models/entities/Order";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { Role } from "../../../core/models/entities/Role";
import { enviroment } from "../../../enviroment";

// Stripe prepayment (vendor orgs with credit limit 0): the API parks the
// order in "Awaiting payment" and persists a Stripe Checkout link on it.
// Only render payment UI when the link actually exists - an order can carry
// the status without a URL (e.g. the Checkout session could not be created);
// those rows just show the status badge and the mail/webhook flow covers them.
export function orderAwaitsStripePayment(order: Order): boolean {
  return (
    order.status === OrderStatus.AwaitingPayment && !!order.stripePaymentUrl
  );
}

// Owner's decision: an END-CUSTOMER (EndUser) must never meet Stripe — for an
// end-customer order the VENDOR pays. So all "Pay now" surfaces (button, notice
// banner, order-list action, and the OrderPlaced auto-redirect) are gated to
// Vendor and Admin roles only. A vendor viewing a customer's order keeps the
// action (role is Vendor); an end user never sees it, regardless of whose order
// it is. An undefined role (profile not yet loaded) resolves to false — the safe
// default is to withhold the payment link.
export function useCanPayOrders(): boolean {
  const { role } = useProfileContext();
  return role === Role.Vendor || role === Role.Admin;
}

// Admin-only "Cancel order" surface (order list): vendors and end customers
// never cancel parked orders - the action kills the payment link, which is the
// vendor's only way to complete the order. Gated on the FE flag so the button
// can ship dark until the API flag (AdminOrderCancelEnabled) is on.
export function useCanCancelAwaitingOrders(): boolean {
  const { role } = useProfileContext();
  return enviroment.orderCancelEnabled && role === Role.Admin;
}

// New tab + noopener: the Checkout page must not get a handle back to the
// app window, and the order screen stays open so the user returns to it
// after paying (the Stripe success URL redirects the checkout tab itself).
export function openStripePayment(url: string): void {
  window.open(url, "_blank", "noopener,noreferrer");
}

interface PayNowProps {
  order: Order;
  className?: string;
}

// Primary "Pay now" action. Self-gating: renders nothing unless the current
// user may pay (Vendor/Admin, never an end customer) AND the order is awaiting
// payment AND has a payment link, so callers can drop it into any button row
// without repeating the gate.
export function PayNowButton({ order, className }: PayNowProps) {
  const { t } = useTranslation();
  const canPay = useCanPayOrders();
  if (!canPay || !orderAwaitsStripePayment(order)) return null;
  return (
    <PrimaryButton
      className={className}
      onClick={() => openStripePayment(order.stripePaymentUrl!)}
    >
      <FontAwesomeIcon icon={faCreditCard} className={"mr-2"} />
      {t("orderPayment.payNow")}
    </PrimaryButton>
  );
}

// Banner explaining why the order is parked, with an optional inline Pay now
// button. Same self-gating contract as PayNowButton — including the Vendor/Admin
// role gate, so an end customer never sees the "payment required" banner (they
// still see the plain "Awaiting payment" status badge, just no link to pay).
export function AwaitingPaymentNotice({
  order,
  showButton,
  className,
}: PayNowProps & { showButton?: boolean }) {
  const { t } = useTranslation();
  const canPay = useCanPayOrders();
  if (!canPay || !orderAwaitsStripePayment(order)) return null;
  return (
    <div
      className={`flex items-center justify-between gap-4 rounded border border-orange-300 bg-orange-50 px-4 py-3 ${
        className ?? ""
      }`}
    >
      <div>
        <p className={"font-semibold text-orange-700"}>
          {t("orderPayment.paymentRequired")}
        </p>
        <p className={"text-sm text-orange-700"}>
          {t("orderPayment.prepaymentNotice")}
        </p>
      </div>
      {showButton && <PayNowButton order={order} />}
    </div>
  );
}
